import { useState, useEffect } from 'react';
import { API } from '../../api';
import { withLanguage } from '../common/Texts';
import { getPotentialTalentArray, curvePickForReply } from '../../Jacques';
import {
    validateDynamicAnswers,
    coerceDynamicAnswersForSubmit,
    fieldsApplicableToReply,
} from '../../utils/dynamicFields';
import { alternativeErrorsFromResponse, validateAlternatives } from '../../utils/alternativeLabels';

const INITIAL_STATE = {
    loaded: false,
    company: {},
    questionnaire: null,
    nombre: '',
    dob: '',
    user_generic_id: '',
    posicion: '',
    areafunc: '',
    business_unit: '',
    grupo_interno: 0,
    nivelCalc: '',
    subnivelCalc: '',
    nivelLabel: '',
    subnivelLabel: '',
    alternativeLabels: [],
    alternativeErrors: [],
    nivelPot: '',
    duration_seconds: 0,
    status: '',
    invalid: false,
    published: false,
    performance: '',
    id_label: '',
    positions: [],
    departments: [],
    business_units: [],
    grupos_internos: [],
    questions: [],
    answers: [],
    dynamic_fields: [],
    dynamic_answers: {},
    dynamic_errors: {},
    generic_id_error: '',
    save_error: '',
    replyData: null,
    age_at_reply: null,
    created_date: null,
};

export default function useLabelData(replyID, language, navigate) {
    const [state, setState] = useState(INITIAL_STATE);

    useEffect(() => {
        if (!replyID) return;
        API.get(`/reply/${replyID}/`).then((response) => {
            const d = response.data;
            const cd = d.questionnaire.company;
            const grupos_internos = [...cd.grupos_internos, { id: 0, name: ' ' }];

            const {
                name, position, department, level_calc, sublevel_calc,
                level_label, sublevel_label, dob, user_generic_id, business_unit,
                grupo_interno, duration_seconds, status, invalid, performance,
                published, age_at_reply, alternative_labels,
            } = d;

            const t = withLanguage(language);
            let nivelPot = '';
            const effectiveLevel = level_calc != null ? level_calc : level_label;
            const effectiveSublevel = level_calc != null ? sublevel_calc : sublevel_label;
            if (effectiveLevel && effectiveSublevel && age_at_reply != null) {
                const [nivelPotCurva] = getPotentialTalentArray(
                    age_at_reply, effectiveLevel + effectiveSublevel, t, curvePickForReply(d.created_date),
                );
                nivelPot = nivelPotCurva;
            }

            const dynamic_fields = fieldsApplicableToReply(cd.dynamic_fields, d.created_date);
            const dynamic_answers = { ...(d.dynamic_answers || {}) };
            dynamic_fields.forEach(f => {
                if (!(f.key in dynamic_answers)) dynamic_answers[f.key] = '';
            });

            const answers = d.answers;
            const questions = answers.map(ans => d.questionnaire.questions.find(q => q.id === ans.question));

            setState({
                loaded: true,
                company: { id: cd.id, name: cd.name, chart: cd.chart },
                questionnaire: d.questionnaire,
                nombre: name || '',
                dob: dob || '',
                user_generic_id: user_generic_id || '',
                posicion: position || '',
                areafunc: department || '',
                business_unit: business_unit || '',
                grupo_interno: grupo_interno || 0,
                nivelCalc: level_calc || '',
                subnivelCalc: sublevel_calc || '',
                nivelLabel: level_label || '',
                subnivelLabel: sublevel_label || '',
                alternativeLabels: (alternative_labels || []).map(({ level, sublevel }) => ({ level, sublevel })),
                alternativeErrors: [],
                nivelPot,
                duration_seconds: duration_seconds || 0,
                status: status || '',
                invalid: !!invalid,
                published: !!published,
                performance: performance !== null && performance !== undefined ? performance : '',
                id_label: cd.id_label || '',
                positions: cd.positions || [],
                departments: cd.departments || [],
                business_units: cd.business_units || [],
                grupos_internos,
                questions,
                answers,
                dynamic_fields,
                dynamic_answers,
                dynamic_errors: {},
                generic_id_error: '',
                replyData: { level_calc, sublevel_calc, level_label, sublevel_label, age_at_reply },
                age_at_reply: age_at_reply ?? null,
                created_date: d.created_date || null,
            });
        }).catch(err => console.error('Error en Api:', err));
    }, [replyID, language]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setState(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'user_generic_id' ? { generic_id_error: '' } : {}),
        }));
    };

    const handleDynamicChange = (key) => (event) => {
        const value = event.target.value;
        setState(prev => {
            const t = withLanguage(language);
            const dynamic_answers = { ...prev.dynamic_answers, [key]: value };
            const { errors } = validateDynamicAnswers(prev.dynamic_fields, dynamic_answers, t);
            return { ...prev, dynamic_answers, dynamic_errors: errors };
        });
    };

    const handleToggle = (field) => () => {
        setState(prev => ({ ...prev, [field]: !prev[field] }));
    };

    // Los errores de las alternativas se muestran recién al intentar guardar;
    // editar cualquier fila los limpia para no marcar algo que ya se corrigió.
    const setAlternatives = (update) => {
        setState(prev => ({ ...prev, alternativeLabels: update(prev.alternativeLabels), alternativeErrors: [] }));
    };

    const addAlternative = () => setAlternatives(alts => [...alts, { level: '', sublevel: '' }]);

    const changeAlternative = (index, field, value) => setAlternatives(alts => (
        alts.map((alt, i) => (i === index ? { ...alt, [field]: value } : alt))
    ));

    const removeAlternative = (index) => setAlternatives(alts => alts.filter((_, i) => i !== index));

    const save = (allowLabel) => {
        const t = withLanguage(language);
        const { valid: dynValid, errors: dynamic_errors } = validateDynamicAnswers(
            state.dynamic_fields, state.dynamic_answers, t
        );
        if (!dynValid) {
            setState(prev => ({ ...prev, dynamic_errors }));
            return;
        }
        if (allowLabel) {
            const alternativeErrors = validateAlternatives(
                state.nivelLabel, state.subnivelLabel, state.alternativeLabels
            );
            if (alternativeErrors.some(Boolean)) {
                setState(prev => ({ ...prev, alternativeErrors }));
                return;
            }
        }
        setState(prev => ({ ...prev, generic_id_error: '', save_error: '' }));

        const data = {
            position: state.posicion,
            department: state.areafunc,
            business_unit: state.business_unit,
            grupo_interno: state.grupo_interno,
            dob: state.dob,
            name: state.nombre,
            user_generic_id: state.user_generic_id,
            invalid: state.invalid,
        };

        if (state.dynamic_fields.length > 0) {
            data.dynamic_answers = coerceDynamicAnswersForSubmit(state.dynamic_fields, state.dynamic_answers);
        }

        if (allowLabel) {
            if (state.nivelLabel !== '') data.level_label = state.nivelLabel;
            if (state.subnivelLabel !== '') data.sublevel_label = state.subnivelLabel;
            data.published = state.published;
            data.alternative_labels = state.alternativeLabels;
        }

        if (data.grupo_interno == 0) data.grupo_interno = null;

        const url = allowLabel ? `/reply/${replyID}/label` : `/reply/${replyID}/`;

        API.put(url, data)
            .then(() => {
                const performanceValue = state.performance !== '' ? state.performance : null;
                return API.put(`/reply/${replyID}/performance/`, { performance: performanceValue });
            })
            .then(() => navigate('/results'))
            .catch(error => {
                const errs = error?.response?.data;
                const idErr = errs?.user_generic_id;
                const altErrors = allowLabel
                    ? alternativeErrorsFromResponse(errs?.alternative_labels, state.alternativeLabels.length)
                    : [];
                if (idErr) {
                    const key = Array.isArray(idErr) ? idErr[0] : idErr;
                    setState(prev => ({ ...prev, generic_id_error: t(key) || key }));
                }
                if (altErrors.some(Boolean)) {
                    setState(prev => ({ ...prev, alternativeErrors: altErrors }));
                } else if (errs?.alternative_labels) {
                    // Sin filas donde mostrarlo (p. ej. la principal choca con
                    // alternativas guardadas que ya no están en pantalla).
                    const code = [].concat(errs.alternative_labels)[0];
                    setState(prev => ({ ...prev, save_error: t(code) || t('label_save_error') }));
                }
                if (!idErr && !errs?.alternative_labels) {
                    console.error('Error en Api:', error);
                    setState(prev => ({ ...prev, save_error: t('label_save_error') }));
                }
            });
    };

    return {
        ...state, handleChange, handleDynamicChange, handleToggle,
        addAlternative, changeAlternative, removeAlternative, save,
    };
}
